import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecommendations, StudyRecommendation } from '../../services/api';
import { storage } from '../../services/storage';

interface PrimaryActionCardProps {
  studentId: string;
  totalAttempted?: number;
}

const TYPE_ICONS: Record<string, string> = {
  complete_reviews: '\uD83D\uDD04', // refresh
  struggling_lo: '\uD83C\uDFAF',    // target
  gate_prep: '\uD83D\uDEAA',        // door
  new_content: '\uD83D\uDCDA',      // books
  challenge_mode: '\u26A1',         // lightning
};

const TYPE_COLORS: Record<string, { bg: string; accent: string; text: string }> = {
  complete_reviews: { bg: '#eff6ff', accent: '#3b82f6', text: '#1e40af' },
  struggling_lo: { bg: '#fef2f2', accent: '#ef4444', text: '#991b1b' },
  gate_prep: { bg: '#f5f3ff', accent: '#8b5cf6', text: '#5b21b6' },
  new_content: { bg: '#f0fdf4', accent: '#22c55e', text: '#166534' },
  challenge_mode: { bg: '#fffbeb', accent: '#f59e0b', text: '#92400e' },
};

export const PrimaryActionCard: React.FC<PrimaryActionCardProps> = ({ studentId, totalAttempted = 0 }) => {
  const [recommendations, setRecommendations] = useState<StudyRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  // Only show welcome if: no localStorage flag AND no prior activity
  const [showWelcome, setShowWelcome] = useState(!storage.hasSeenTip('welcome') && totalAttempted === 0);
  const navigate = useNavigate();

  useEffect(() => {
    getRecommendations(studentId, 4)
      .then((data) => {
        setRecommendations(data.recommendations);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [studentId]);

  const handleStartFirstLesson = () => {
    storage.markTipSeen('welcome');
    setShowWelcome(false);
    navigate('/practice/1/1');
  };

  if (loading) {
    return (
      <div className="primary-action-card loading">
        <div className="loading-pulse" />
      </div>
    );
  }

  // Welcome banner for first-time users
  if (showWelcome) {
    return (
      <div className="primary-action-wrapper">
        <div className="primary-action-card welcome-banner">
          <div className="welcome-content">
            <div className="welcome-header">Welcome to RepsLearn! 👋</div>
            <p className="welcome-text">
              You're starting with Unit 1. Each lesson has practice questions — answer them,
              and we'll tell you right away how you did. Get a question wrong? You'll get a
              chance to try again or try a similar one. As you practice, we'll schedule
              reviews so you don't forget what you've learned.
            </p>
          </div>
          <button className="primary-action-button welcome-button" onClick={handleStartFirstLesson}>
            Start Your First Lesson →
          </button>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="primary-action-card all-done">
        <span className="all-done-icon">&#10003;</span>
        <div className="all-done-text">
          <strong>You're all caught up!</strong>
          <span>No urgent tasks right now. Great job!</span>
        </div>
      </div>
    );
  }

  const primary = recommendations[0];
  const secondary = recommendations.slice(1);
  const colors = TYPE_COLORS[primary.type] || TYPE_COLORS.new_content;

  const handleClick = (rec: StudyRecommendation) => {
    navigate(rec.action.link);
  };

  const hasSecondary = secondary.length > 0;

  return (
    <div className="primary-action-wrapper">
      <div
        className={`primary-action-card ${hasSecondary ? 'has-secondary' : ''}`}
        style={{ backgroundColor: colors.bg }}
      >
        <div className="primary-action-content">
          <span className="primary-action-icon">{TYPE_ICONS[primary.type]}</span>
          <div className="primary-action-text">
            <span className="primary-action-label" style={{ color: colors.text }}>
              Ready to continue?
            </span>
            <strong className="primary-action-title">{primary.title}</strong>
            {primary.estimated_time_minutes > 0 && (
              <span className="primary-action-time">
                ~{primary.estimated_time_minutes} min
              </span>
            )}
          </div>
        </div>
        <button
          className="primary-action-button"
          style={{ backgroundColor: colors.accent }}
          onClick={() => handleClick(primary)}
        >
          {primary.action.text}
        </button>
      </div>

      {secondary.length > 0 && (
        <div className="secondary-actions">
          <button
            className="secondary-toggle"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Hide suggestions' : `${secondary.length} more suggestion${secondary.length > 1 ? 's' : ''}`}
            <span className={`toggle-arrow ${expanded ? 'expanded' : ''}`}>&#9662;</span>
          </button>

          {expanded && (
            <div className="secondary-list">
              {secondary.map((rec, idx) => {
                const recColors = TYPE_COLORS[rec.type] || TYPE_COLORS.new_content;
                return (
                  <div
                    key={idx}
                    className="secondary-item"
                    onClick={() => handleClick(rec)}
                    style={{ borderLeftColor: recColors.accent }}
                  >
                    <span className="secondary-icon">{TYPE_ICONS[rec.type]}</span>
                    <div className="secondary-text">
                      <span className="secondary-title">{rec.title}</span>
                      <span className="secondary-desc">{rec.description}</span>
                    </div>
                    <span className="secondary-time">~{rec.estimated_time_minutes}m</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PrimaryActionCard;
